import React from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { ShieldCheck, ShieldQuestion, ShieldX, Loader2 } from "lucide-react";

const BOOKING_COPY = {
  pending: {
    icon: ShieldQuestion,
    title: "One last step after payment",
    body: "You can complete your booking now. After payment, we'll ask you to upload your ID and driver's license to finalise the process. It usually takes under 24 hours.",
    cta: "Continue to checkout",
    allowContinue: true,
  },
  submitted: {
    icon: Loader2,
    title: "Verification in progress",
    body: "Your documents are under review. We'll email you as soon as it's approved — usually within 24 hours.",
    cta: "View status",
    animatedIcon: true,
    allowContinue: false,
  },
  rejected: {
    icon: ShieldX,
    title: "Verification needs another look",
    body: "Our team couldn't verify your previous documents. Please re-upload clearer copies to continue.",
    cta: "Re-upload documents",
    allowContinue: false,
  },
};

const LISTING_COPY = {
  pending: {
    icon: ShieldQuestion,
    title: "Your car is submitted!",
    body: "We've received your listing, but it won't be visible to renters until two steps are done: identity verification and admin review. You can verify now or later from your dashboard — once verified, your listing moves into admin review and goes live after approval.",
    cta: "Verify identity now",
    allowContinue: true,
  },
  submitted: {
    icon: Loader2,
    title: "Verification in progress",
    body: "Your identity documents are under review. We'll email you as soon as they're approved — usually within 24 hours. Once approved, your car listing will then move into admin review before going live.",
    cta: "View status",
    animatedIcon: true,
    allowContinue: false,
  },
  rejected: {
    icon: ShieldX,
    title: "Verification needs another look",
    body: "Our team couldn't verify your previous documents. Please re-upload clearer copies so you can receive payouts.",
    cta: "Re-upload documents",
    allowContinue: false,
  },
};

export default function KycRequiredDialog({ open, onOpenChange, kycStatus = "pending", redirectTo, onContinueAnyway, context = "booking" }) {
  const nav = useNavigate();
  const COPY = context === "listing" ? LISTING_COPY : BOOKING_COPY;
  const copy = COPY[kycStatus] || COPY.pending;
  const Icon = copy.icon;

  const goVerify = () => {
    onOpenChange(false);
    nav(redirectTo || "/account/kyc");
  };

  const continueAnyway = () => {
    onOpenChange(false);
    if (onContinueAnyway) onContinueAnyway();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="kyc-required-dialog">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-lg bg-primary text-primary-foreground flex items-center justify-center mb-3">
            <Icon size={20} className={copy.animatedIcon ? "animate-spin" : ""} />
          </div>
          <DialogTitle className="font-display text-center">{copy.title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground text-center leading-relaxed">{copy.body}</p>
        <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-center mt-2 gap-2">
          {copy.allowContinue ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="kyc-dialog-cancel">
                {context === "listing" ? "Maybe later" : "Cancel"}
              </Button>
              <Button onClick={context === "listing" ? goVerify : continueAnyway} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="kyc-dialog-continue">
                <ShieldCheck size={14} className="mr-1.5" /> {copy.cta}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="kyc-dialog-cancel">Not now</Button>
              <Button onClick={goVerify} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="kyc-dialog-cta">
                <ShieldCheck size={14} className="mr-1.5" /> {copy.cta}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
